/**
 * 콘텐츠 코드(content_code) → 평가결과 UI 뷰(TriageView 등) 매핑
 * 커리큘럼 시나리오 필터용 코드 목록은 백엔드 scenario_types와 동기화.
 */

/** 커리큘럼·필터용 콘텐츠 코드 (백엔드 app.config scenario_types) */
export type ScenarioTypeCatalogCode =
	| 'triage'
	| 'gcs'
	| 'pupil_reaction'
	| 'motor_power'
	| 'iv_injection'
	| 'blood_draw'
	| 'acls'
	| 'radiation';

export interface ScenarioTypeCatalogItem {
	code: ScenarioTypeCatalogCode;
	label: string;
	/** 그룹 키: neurological_assessment | scenario_injections | scenario_acls 등 */
	group?: string;
}

/** 커리큘럼 시나리오 타입 필터(스튜디오 등) */
export const SCENARIO_TYPE_CATALOG: ScenarioTypeCatalogItem[] = [
	{ code: 'triage', label: '중증도분류' },
	{ code: 'gcs', label: 'GCS', group: 'neurological_assessment' },
	{ code: 'pupil_reaction', label: '동공반응', group: 'neurological_assessment' },
	{ code: 'motor_power', label: '근력사정', group: 'neurological_assessment' },
	{ code: 'iv_injection', label: '정맥주사', group: 'scenario_injections' },
	{ code: 'blood_draw', label: '채혈', group: 'scenario_injections' },
	{ code: 'acls', label: 'ACLS', group: 'scenario_acls' },
	{ code: 'radiation', label: '방사선' },
];

const scenarioTypeCatalogMap = new Map<ScenarioTypeCatalogCode, ScenarioTypeCatalogItem>();
SCENARIO_TYPE_CATALOG.forEach((c) => scenarioTypeCatalogMap.set(c.code, c));

export function getScenarioTypeCatalogItem(code: string): ScenarioTypeCatalogItem | undefined {
	return scenarioTypeCatalogMap.get(code as ScenarioTypeCatalogCode);
}

export function isScenarioTypeCatalogCode(code: string): code is ScenarioTypeCatalogCode {
	return scenarioTypeCatalogMap.has(code as ScenarioTypeCatalogCode);
}

/** 앱 평가결과 본문 뷰 키. 시트 `result_view`는 ResultTableView 모듈에서 TriageResult 등으로 올 수 있음 */
export type ResultViewType =
	| 'TriageView'
	/** GCS·동공·근력 표를 한 본문에 나열 (레거시 시트 `AssesmentView` 오타는 normalize에서 여기로 치환) */
	| 'NeurologicalBundleView'
	| 'GcsResult'
	| 'PupilResult'
	| 'MotorResult'
	| 'ProcedureView'
	| 'EvaluationTable'
	| 'AoEvaluationView'
	| 'Covid19View'
	| 'AclsView';

/**
 * [old_scenario_data](https://docs.google.com/spreadsheets/d/1kJc2ljIZpu3KWdbBuGf7SQTNKVLK9Tp9MyTPOuBg4lU/edit?gid=2055225077) A열 `content_code` → 앱 본문 뷰.
 * 시트 F열: TriageResult | AssessmentResult | ProcedureResult | EvaluationTable → ResultViewType으로 대응.
 */
const LS_CORE_NURSING_CODES: Record<string, ResultViewType> = Object.fromEntries(
	Array.from({ length: 20 }, (_, i) => {
		const id = String(i + 1).padStart(6, '0');
		return [`ls${id}`, 'ProcedureView' as ResultViewType];
	}),
) as Record<string, ResultViewType>;

export const OLD_SCENARIO_DATA_CONTENT_CODE_TO_VIEW: Record<string, ResultViewType> = {
	/** TriageResult */
	ktas: 'TriageView',
	triage: 'TriageView',
	radiation: 'TriageView',
	/** AssessmentResult (mode_key로 gcs·동공·근력 구분) */
	neurological_assessment: 'NeurologicalBundleView',
	/** ProcedureResult */
	medication: 'ProcedureView',
	diagnostic_test: 'ProcedureView',
	ppe: 'ProcedureView',
	/** Covid19View & AclsView */
	covid19: 'Covid19View',
	acls: 'AclsView',
	/** AoEvaluationView (질환별 환자간호) */
	pneumonia: 'AoEvaluationView',
	bradycardia: 'AoEvaluationView',
	antepartumcare: 'AoEvaluationView',
	intrapartumcare: 'AoEvaluationView',
	evarixbleeding: 'AoEvaluationView',
	electrolyteimbalance: 'AoEvaluationView',
	acutemyocardialinfarction: 'AoEvaluationView',
	gestationaldiabetes: 'AoEvaluationView',
	...LS_CORE_NURSING_CODES,
	/** EvaluationTable (증상별 의사결정) */
	S01_ED_Hypoglycemia: 'EvaluationTable',
	S02_ED_Highfever: 'EvaluationTable',
	S03_LowBP: 'EvaluationTable',
	S04_ChestPain: 'EvaluationTable',
	S05_Arrhythmia: 'EvaluationTable',
	S06_NauseaVomit: 'EvaluationTable',
	S07_GIbleeding: 'EvaluationTable',
	S08_Dyspnea: 'EvaluationTable',
	S09_Ped_Pneumonia: 'EvaluationTable',
	S10_Hypertension_Headache: 'EvaluationTable',
	S11_Blood_Poisoning: 'EvaluationTable',
	S12_Hypertension_Headache: 'EvaluationTable',
};

// ─── scenarioResultService.ts / ScenarioEvaluationResultList.tsx 에서 사용하는 레거시 types ───

/** 시나리오 평가결과 타입 코드 */
export type ScenarioTypeCode =
	| 'triage'
	| 'gcs'
	| 'pupil_reaction'
	| 'motor_power'
	| 'si'
	| 'iv_injection'
	| 'ktas'
	| 'radiation';

/** 시나리오 타입 설정 */
export interface ScenarioTypeConfig {
	code: ScenarioTypeCode;
	label: string;
	pathTemplate: string;
	resultView: ResultViewType;
	/** pathTemplate의 {param} 변수 키 배열 */
	pathParams: string[];
}

const SCENARIO_TYPE_CONFIG_MAP: Record<ScenarioTypeCode, ScenarioTypeConfig> = {
	triage: { code: 'triage', label: '중증도분류', pathTemplate: '/{lang}/result/triage', resultView: 'TriageView', pathParams: [] },
	ktas: { code: 'ktas', label: 'KTAS', pathTemplate: '/{lang}/result/triage', resultView: 'TriageView', pathParams: [] },
	radiation: { code: 'radiation', label: '방사선', pathTemplate: '/{lang}/result/triage', resultView: 'TriageView', pathParams: [] },
	gcs: { code: 'gcs', label: 'GCS', pathTemplate: '/{lang}/result/gcs', resultView: 'GcsResult', pathParams: [] },
	pupil_reaction: { code: 'pupil_reaction', label: '동공반응', pathTemplate: '/{lang}/result/pupil', resultView: 'PupilResult', pathParams: [] },
	motor_power: { code: 'motor_power', label: '근력사정', pathTemplate: '/{lang}/result/motor', resultView: 'MotorResult', pathParams: [] },
	si: { code: 'si', label: '정맥주사', pathTemplate: '/{lang}/result/si/{contentId}', resultView: 'ProcedureView', pathParams: ['contentId'] },
	iv_injection: { code: 'iv_injection', label: '정맥주사', pathTemplate: '/{lang}/result/si/{contentId}', resultView: 'ProcedureView', pathParams: ['contentId'] },
};

export const SCENARIO_TYPES = Object.values(SCENARIO_TYPE_CONFIG_MAP);

export function getScenarioTypeConfig(code: string): ScenarioTypeConfig | undefined {
	return SCENARIO_TYPE_CONFIG_MAP[code as ScenarioTypeCode];
}

export function buildEvaluationResultPath(config: ScenarioTypeConfig, pathParams: Record<string, string>): string {
	let path = config.pathTemplate;
	for (const [k, v] of Object.entries(pathParams)) {
		path = path.replace(`{${k}}`, v);
	}
	return path;
}

/** 핵심간호술기(랩 시뮼) old_scenario_data content_code `ls000001` ~ `ls000020` */
export function isLsCoreNursingProcedureContentCode(code: string | null | undefined): boolean {
	if (code == null || String(code).trim() === '') return false;
	const m = /^ls(\d{6})$/.exec(String(code).trim());
	if (!m) return false;
	const n = Number.parseInt(m[1], 10);
	return n >= 1 && n <= 20;
}

/** 질환별 환자간호 old_scenario_data content_code (pneumonia, bradycardia 등) */
const AO_DISEASE_CARE_CONTENT_CODES = new Set<string>([
	'pneumonia',
	'bradycardia',
	'antepartumcare',
	'intrapartumcare',
	'evarixbleeding',
	'electrolyteimbalance',
	'acutemyocardialinfarction',
	'gestationaldiabetes',
]);

export function isAoDiseaseCareContentCode(code: string | null | undefined): boolean {
	if (code == null || String(code).trim() === '') return false;
	return AO_DISEASE_CARE_CONTENT_CODES.has(String(code).trim());
}

/** 증상별 의사결정 old_scenario_data content_code (S01~S11) */
export function isMdSymptomDecisionContentCode(code: string | null | undefined): boolean {
	if (code == null || String(code).trim() === '') return false;
	const key = String(code).trim();
	return /^S\d{2}_/.test(key);
}

/**
 * `content_code` → 결과 화면 뷰. 우선 old_scenario_data 시트 매핑, 이후 mode_key·카탈로그용 보조 규칙.
 */
export function resolveResultViewFromContentCode(
	contentCode: string | undefined | null,
): ResultViewType | undefined {
	if (contentCode == null || String(contentCode).trim() === '') return undefined;
	const key = String(contentCode).trim();

	const fromSheet = OLD_SCENARIO_DATA_CONTENT_CODE_TO_VIEW[key];
	if (fromSheet) return fromSheet;

	/** 시트에 없는 API·레거시 mode_key (neurological_assessment 하위) */
	if (key === 'gcs') return 'GcsResult';
	if (key === 'pupil_reaction') return 'PupilResult';
	if (key === 'motor_power') return 'MotorResult';
	if (/^S\d{2}_/.test(key)) return 'EvaluationTable';
	if (isScenarioTypeCatalogCode(key)) return 'ProcedureView';
	return undefined;
}
